/**
 * artworkUploader — direct-to-S3 multipart upload orchestrator.
 *
 * Implements the 4-step upload flow:
 *
 *   1. INITIATE — POST `/uploads/initiate-multipart` to receive an upload id,
 *      S3 key, and pre-signed part URLs from the platform.
 *   2. UPLOAD PARTS — PUT each chunk directly to its pre-signed S3 URL.
 *   3. COMPLETE — POST `/uploads/complete-multipart` (upload id + key only)
 *      to finalise the S3 multipart object. The platform reads the per-part
 *      etags back from S3 via ListParts, so the request body stays small
 *      regardless of file size (the edge WAF blocks bodies over 8 KB).
 *   4. REGISTER — POST `/account/artwork` to create the `ArtworkFile` record
 *      and receive the server-confirmed {@link ArtworkFileDTO}.
 *
 * Cancellation is plumbed via `AbortSignal`: every network call honours the
 * supplied signal so a single `controller.abort()` tears down in-flight
 * chunk PUTs and any pending platform calls.
 *
 * Progress is reported as each part completes (0–100 over the platform
 * calls). The `onProgress` callback fires after every PUT so the parent can
 * update the UI incrementally.
 *
 * SF-CONTOUR-105 — resilience on a dropped connection:
 *   - Each part PUT retries on a transient failure (network error, HTTP 429,
 *     or 5xx) with exponential backoff, up to {@link DEFAULT_MAX_PART_RETRIES}
 *     attempts. A permanent failure (4xx other than 429 — an expired
 *     presigned URL, a bad request) is NOT retried; it fails immediately so
 *     the caller can re-initiate.
 *   - `onSessionUpdate` fires after the initiate step and after every
 *     completed part with a serializable {@link UploadSessionSnapshot}. A
 *     caller that persists this (e.g. to `sessionStorage`) can pass it back
 *     in as `resumeSession` on a later call to skip the initiate step and
 *     every already-completed part — resuming a dropped upload instead of
 *     restarting from byte zero. The resumable wrapper that owns this
 *     persistence lives in `@dfq/shared/lib/uploadResumeStore` +
 *     `createResumableUploadRunner` (containers only — never imported by a
 *     capsule).
 *
 * Per `.claude/rules/frontend-data-flow.md`, axios lives only in
 * `@dfq/shared/api/*`. The FileUploader capsule never imports a network
 * primitive; it receives this function (or a test stub) via the `runner`
 * prop injected by its parent container.
 */
import type { ArtworkFileDTO } from '@dfq/shared/stores/artworkSlice';
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';

// ----------------------------------------------------------------------------
// Wire-shape types (match platform DTO toArray() output exactly)
// ----------------------------------------------------------------------------

/** One pre-signed part entry returned by the initiate step. */
export interface InitiateUploadPart {
  partNumber: number;
  presignedUrl: string;
  /** Byte count for this part (last part may be smaller). */
  sizeBytes: number;
}

/** Response data nested under `data.upload` from the initiate endpoint. */
export interface InitiateUploadData {
  uploadId: string;
  s3Key: string;
  bucket: string;
  parts: InitiateUploadPart[];
  partSizeBytes: number;
  totalParts: number;
  presignedUrlExpiresAt: number;
}

/** Response data nested under `data.upload` from the complete endpoint. */
export interface CompleteUploadData {
  s3Key: string;
  bucket: string;
  s3VersionId: string | null;
  etag: string;
  location: string;
}

// ----------------------------------------------------------------------------
// Public option types
// ----------------------------------------------------------------------------

/**
 * Options accepted by {@link uploadArtworkFile}. `file` is required;
 * everything else has a sensible default so a basic `await
 * uploadArtworkFile({ file })` works in tests without extra setup.
 */
/**
 * What an upload is for. Decides the object-key prefix and which types the
 * platform accepts — a damage photo is not filed under `artwork-uploads/`, and
 * artwork does not accept a HEIC snapshot from a phone.
 */
export type UploadPurpose = 'artwork' | 'damage_claim';

/**
 * Serializable snapshot of an in-progress multipart upload session —
 * SF-CONTOUR-105. Everything a caller needs to resume without re-initiating:
 * the S3 coordinates, the presigned part URLs (until they expire), and which
 * part numbers already landed. `fileIdentity` guards against resuming a
 * session against a different file that happens to reuse the same queue slot.
 */
export interface UploadSessionSnapshot {
  uploadId: string;
  s3Key: string;
  bucket: string;
  parts: InitiateUploadPart[];
  partSizeBytes: number;
  totalParts: number;
  presignedUrlExpiresAt: number;
  /** Part numbers (1-indexed) whose PUT has already completed successfully. */
  completedPartNumbers: number[];
  purpose: UploadPurpose;
  basePath: string;
  fileIdentity: {
    name: string;
    sizeBytes: number;
    lastModified: number;
  };
}

/** Default ceiling on per-part retry attempts for a transient failure. */
export const DEFAULT_MAX_PART_RETRIES = 3;

/** Default base delay (ms) for exponential backoff between part retries. */
export const DEFAULT_PART_RETRY_BASE_DELAY_MS = 500;

export interface UploadToS3Options {
  file: File;
  /** Defaults to `artwork`. */
  purpose?: UploadPurpose;
  onProgress?: (update: {
    percent: number;
    bytesUploaded: number;
    bytesTotal: number;
  }) => void;
  signal?: AbortSignal;
  /**
   * Overrides the `/uploads` initiate/complete endpoint prefix. Defaults to
   * `/uploads` (authenticated). SF-QUOTE-103 passes `/uploads/guest` for the
   * public quote-request flow's guest upload path — same request/response
   * shape, no account required, ownership bound to the session cookie
   * instead of a user id.
   */
  basePath?: string;
  /**
   * A previously-persisted session for the SAME file (matched via
   * `fileIdentity`) — SF-CONTOUR-105. When present, valid (identity matches)
   * and not expired (`presignedUrlExpiresAt` still in the future), the
   * initiate step is skipped and every part in `completedPartNumbers` is
   * skipped, so a retry after a dropped connection resumes from the next
   * incomplete part instead of restarting. An expired or mismatched session
   * is ignored and a fresh upload starts normally.
   */
  resumeSession?: UploadSessionSnapshot | null;
  /**
   * Fired after the initiate step and after every completed part with a
   * snapshot the caller can persist (e.g. `sessionStorage`) and pass back in
   * as `resumeSession` on a later call.
   */
  onSessionUpdate?: (session: UploadSessionSnapshot) => void;
  /** Ceiling on retry attempts for a transient per-part failure. */
  maxPartRetries?: number;
  /** Base delay (ms) for exponential backoff between part retries. */
  partRetryBaseDelayMs?: number;
}

export interface UploadArtworkFileOptions {
  /** The user-selected file. */
  file: File;
  /**
   * Fired after each part completes. `percent` is in [0, 100] across
   * the full upload. The final 100 % tick fires after the register call
   * resolves so the parent reports completion when the server record exists.
   */
  onProgress?: (update: {
    percent: number;
    bytesUploaded: number;
    bytesTotal: number;
  }) => void;
  /**
   * AbortController signal. Checked before each part PUT; forwarded to
   * every request so a single `controller.abort()` stops the upload.
   */
  signal?: AbortSignal;
  /** SF-CONTOUR-105 — resume a previously-persisted session; see {@link UploadToS3Options.resumeSession}. */
  resumeSession?: UploadSessionSnapshot | null;
  /** SF-CONTOUR-105 — see {@link UploadToS3Options.onSessionUpdate}. */
  onSessionUpdate?: (session: UploadSessionSnapshot) => void;
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

class UploadAbortedError extends Error {
  constructor() {
    super('Upload aborted');
    this.name = 'UploadAbortedError';
  }
}

function assertNotAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new UploadAbortedError();
}

function fileIdentity(file: File): UploadSessionSnapshot['fileIdentity'] {
  return { name: file.name, sizeBytes: file.size, lastModified: file.lastModified };
}

/** True when a persisted session is usable for `file`: same file, presigned URLs not yet expired. */
function sessionUsableFor(session: UploadSessionSnapshot, file: File): boolean {
  const identity = fileIdentity(file);
  return (
    session.fileIdentity.name === identity.name &&
    session.fileIdentity.sizeBytes === identity.sizeBytes &&
    session.fileIdentity.lastModified === identity.lastModified &&
    session.presignedUrlExpiresAt * 1000 > Date.now()
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Transient failures (network error / 429 / 5xx) are worth retrying; anything else fails fast. */
function isTransientStatus(status: number): boolean {
  return status === 429 || status >= 500;
}

/**
 * PUT one part with retry-on-transient-failure. Network errors (fetch
 * rejecting) and HTTP 429/5xx retry with exponential backoff; any other
 * non-2xx (an expired presigned URL, a bad request) throws immediately —
 * retrying a permanent failure only delays the eventual, correct error.
 */
async function putPartWithRetry(
  part: InitiateUploadPart,
  chunk: Blob,
  signal: AbortSignal | undefined,
  maxRetries: number,
  baseDelayMs: number,
): Promise<void> {
  let attempt = 0;

  for (;;) {
    assertNotAborted(signal);

    let response: Response;
    try {
      response = await fetch(part.presignedUrl, { method: 'PUT', body: chunk, signal });
    } catch (cause) {
      if (signal?.aborted) throw new UploadAbortedError();
      if (attempt >= maxRetries) {
        throw new Error(
          `Part ${part.partNumber} upload failed: network error after ${attempt + 1} attempt(s)`,
          { cause },
        );
      }
      await sleep(baseDelayMs * 2 ** attempt);
      attempt += 1;
      continue;
    }

    if (response.ok) return;

    if (!isTransientStatus(response.status) || attempt >= maxRetries) {
      throw new Error(`Part ${part.partNumber} upload failed: HTTP ${response.status}`);
    }

    await sleep(baseDelayMs * 2 ** attempt);
    attempt += 1;
  }
}

// ----------------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------------

/**
 * Upload a single file via the 4-step direct-to-S3 multipart flow and
 * return the server-confirmed {@link ArtworkFileDTO} once registration
 * completes.
 *
 * Throws:
 *   - `UploadAbortedError` when the signal is aborted at any point.
 *   - `ApiError` (from `throwAsApiError`) when a platform call returns
 *     a non-2xx or a well-formed error envelope.
 *   - A plain `Error` with an HTTP status description when an S3 PUT fails.
 */
/**
 * Steps 1-3 only: presign, PUT the parts, complete. Returns the object key.
 *
 * Callers that are not artwork need exactly this and not step 4, which creates
 * an `ArtworkFile` row. Damage-claim photos go through here so their bytes
 * never travel in an API request body — the WAF rejects bodies over 8KB, which
 * every real photo exceeds, and the failure surfaces to the customer as an
 * unexplained "Network Error".
 */
export async function uploadFileToS3(options: UploadToS3Options): Promise<string> {
  const {
    file,
    purpose = 'artwork',
    onProgress,
    signal,
    basePath = '/uploads',
    resumeSession,
    onSessionUpdate,
    maxPartRetries = DEFAULT_MAX_PART_RETRIES,
    partRetryBaseDelayMs = DEFAULT_PART_RETRY_BASE_DELAY_MS,
  } = options;

  assertNotAborted(signal);

  const canResume = resumeSession != null && sessionUsableFor(resumeSession, file);

  // ---- 1. Initiate (skipped when resuming a still-valid session) ----------
  let uploadId: string;
  let s3Key: string;
  let bucket: string;
  let presignedParts: InitiateUploadPart[];
  let partSizeBytes: number;
  let totalParts: number;
  let presignedUrlExpiresAt: number;
  const completedPartNumbers = new Set<number>(canResume ? resumeSession!.completedPartNumbers : []);

  if (canResume) {
    ({ uploadId, s3Key, bucket, parts: presignedParts, partSizeBytes, totalParts, presignedUrlExpiresAt } =
      resumeSession!);
  } else {
    let initiateData: InitiateUploadData;
    try {
      const res = await apiClient.post<ApiEnvelope<{ upload: InitiateUploadData }>>(
        `${basePath}/initiate-multipart`,
        {
          filename: file.name,
          file_size_bytes: file.size,
          mime_type: file.type || 'application/octet-stream',
          ...(purpose === 'artwork' ? {} : { purpose }),
        },
        { signal },
      );
      initiateData = unwrap(res.data, res.status).upload;
    } catch (e) {
      throwAsApiError(e);
    }

    ({ uploadId, s3Key, bucket, parts: presignedParts, partSizeBytes, totalParts, presignedUrlExpiresAt } =
      initiateData!);
  }

  const emitSession = (): void => {
    onSessionUpdate?.({
      uploadId,
      s3Key,
      bucket,
      parts: presignedParts,
      partSizeBytes,
      totalParts,
      presignedUrlExpiresAt,
      completedPartNumbers: Array.from(completedPartNumbers),
      purpose,
      basePath,
      fileIdentity: fileIdentity(file),
    });
  };

  emitSession();

  // ---- 2. Upload each part (skipping ones already completed on resume) ----
  let byteOffset = 0;
  let bytesUploaded = presignedParts
    .filter((p) => completedPartNumbers.has(p.partNumber))
    .reduce((sum, p) => sum + p.sizeBytes, 0);
  const bytesTotal = file.size;

  for (const part of presignedParts) {
    const start = byteOffset;
    const end = byteOffset + part.sizeBytes;
    byteOffset = end;

    if (completedPartNumbers.has(part.partNumber)) continue;

    assertNotAborted(signal);

    const chunk = file.slice(start, end);
    await putPartWithRetry(part, chunk, signal, maxPartRetries, partRetryBaseDelayMs);

    completedPartNumbers.add(part.partNumber);
    bytesUploaded += part.sizeBytes;
    emitSession();

    if (onProgress) {
      // Reserve 5 % of the progress bar for the complete + register calls.
      const percent = Math.min(95, Math.round((bytesUploaded / bytesTotal) * 95));
      onProgress({ percent, bytesUploaded, bytesTotal });
    }
  }

  // ---- 3. Complete --------------------------------------------------------
  assertNotAborted(signal);

  try {
    const res = await apiClient.post<ApiEnvelope<{ upload: CompleteUploadData }>>(
      `${basePath}/complete-multipart`,
      {
        upload_id: uploadId,
        s3_key: s3Key,
      },
      { signal },
    );
    unwrap(res.data, res.status);
  } catch (e) {
    throwAsApiError(e);
  }

  return s3Key;
}

export async function uploadArtworkFile(
  options: UploadArtworkFileOptions,
): Promise<ArtworkFileDTO> {
  const { file, onProgress, signal, resumeSession, onSessionUpdate } = options;

  const s3Key = await uploadFileToS3({
    file,
    purpose: 'artwork',
    onProgress,
    signal,
    resumeSession,
    onSessionUpdate,
  });

  // ---- 4. Register --------------------------------------------------------
  assertNotAborted(signal);

  let artworkDto: ArtworkFileDTO;
  try {
    const res = await apiClient.post<ApiEnvelope<{ artwork: ArtworkFileDTO }>>(
      '/account/artwork',
      {
        original_filename: file.name,
        s3_key: s3Key,
        file_size_bytes: file.size,
        mime_type: file.type || 'application/octet-stream',
      },
      { signal },
    );
    artworkDto = unwrap(res.data, res.status).artwork;
  } catch (e) {
    throwAsApiError(e);
  }

  if (onProgress) {
    onProgress({ percent: 100, bytesUploaded: file.size, bytesTotal: file.size });
  }

  return artworkDto!;
}

/**
 * Guest counterpart of {@link uploadArtworkFile} — SF-QUOTE-103. Same
 * 4-step flow against the `/uploads/guest/*` endpoints, so a visitor can
 * attach artwork to a public quote request before creating an account.
 * Ownership is bound to the caller's session cookie (`apiClient` already
 * sends `withCredentials`), not a user id.
 */
export async function uploadGuestArtworkFile(
  options: UploadArtworkFileOptions,
): Promise<ArtworkFileDTO> {
  const { file, onProgress, signal, resumeSession, onSessionUpdate } = options;

  const s3Key = await uploadFileToS3({
    file,
    purpose: 'artwork',
    onProgress,
    signal,
    basePath: '/uploads/guest',
    resumeSession,
    onSessionUpdate,
  });

  assertNotAborted(signal);

  let artworkDto: ArtworkFileDTO;
  try {
    const res = await apiClient.post<ApiEnvelope<{ artwork: ArtworkFileDTO }>>(
      '/uploads/guest/artwork',
      {
        original_filename: file.name,
        s3_key: s3Key,
        file_size_bytes: file.size,
        mime_type: file.type || 'application/octet-stream',
      },
      { signal },
    );
    artworkDto = unwrap(res.data, res.status).artwork;
  } catch (e) {
    throwAsApiError(e);
  }

  if (onProgress) {
    onProgress({ percent: 100, bytesUploaded: file.size, bytesTotal: file.size });
  }

  return artworkDto!;
}

export { UploadAbortedError };

/**
 * Function shape accepted by the FileUploader capsule for dependency
 * injection. Tests and Storybook stories pass a stub that resolves
 * synchronously without hitting the network.
 */
export type ArtworkUploader = typeof uploadArtworkFile;
